'use strict'

import React, { useEffect, useState } from 'react'
import clamp from 'lodash/clamp'
import { useSprings, animated } from 'react-spring'
import { useDrag } from 'react-use-gesture'

import MealCard from './Feed/MealCard.react';

import './SpringFeed.scss';

const CARD_WIDTH = 295; // px

export function findItemIndex(items, item) {
    return items.findIndex(i => i.mealType === item.mealType && i.date.isSame(item.date, 'day'));
}

export default function SpringFeed(props) {
    // Handle case where activeItem might be undefined
    const getActiveIndex = () => {
        if (!props.activeItem || !props.items.length) return 0;
        const foundIndex = findItemIndex(props.items, props.activeItem);
        return foundIndex >= 0 ? foundIndex : 0;
    };

    let [index, setIndex] = useState(getActiveIndex());

    // Track window width for resize handling
    const [windowWidth, setWindowWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1024
    );

    const [cards, setCard] = useSprings(props.items.length, i => {
        return {
            x: (i - index) * CARD_WIDTH + windowWidth / 2 - CARD_WIDTH / 2,
            sc: 1,
            display: 'block',
        };
    });

    // Handle window resize
    useEffect(() => {
        if (!process.browser) return;

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Recalculate positions when window width changes
    useEffect(() => {
        setCard(i => {
            const x = (i - index) * CARD_WIDTH + windowWidth / 2 - CARD_WIDTH / 2;
            return { x, sc: 1, display: 'block', immediate: false }
        });
    }, [windowWidth, index]);

    useEffect(() => {
        index = getActiveIndex();
        setIndex(index);

        setCard(i => {
            const x = (i - index) * CARD_WIDTH + windowWidth / 2 - CARD_WIDTH / 2;
            // Only skip animation during date changes
            return { x, sc: 1, display: 'block', immediate: true }
        });
    }, [props.startDate, props.endDate]); // only fire this effect when start or end date change

    useEffect(() => {
        index = getActiveIndex();
        setIndex(index);

        setCard(i => {
            const x = (i - index) * CARD_WIDTH + windowWidth / 2 - CARD_WIDTH / 2;
            // Animate smoothly when activeItem changes
            return { x, sc: 1, display: 'block', immediate: false }
        });
    }, [props.activeItem]); // only fire this effect when the activeItem changes

    const bind = useDrag((dragArgs) => {
        let { xy: [x, y], last, initial: [xInitial, yInitial], down, direction: [xDir, yDir],
                distance, cancel } = dragArgs;

        // Compute our difference from where we started to where we are now, x dimension only.
        let xDelta = x - xInitial;

        if (props.isSafariMobile) {
            cancel();
            return;
        }

        if (last) {
            cancel(); // will this stop propagation of the click event? Seems to otherwise.
        }

        // Increase the denominator to increase sensitivity
        const sensitivity = CARD_WIDTH / 5;

        if (down && Math.abs(xDelta) > sensitivity) {
            const newIndex = clamp(index + (xDir > 0 ? -1 : 1), 0, props.items.length - 1);

            // Only trigger navigation if we actually can move and callbacks exist
            if (newIndex !== index) {
                if (xDir > 0 && props.prevCard) {
                    props.prevCard();
                } else if (xDir <= 0 && props.nextCard) {
                    props.nextCard();
                }
            }

            // Mutate index for next drag
            index = newIndex;

            cancel(index);
        }

        setCard(i => {
            const x = (i - index) * CARD_WIDTH + windowWidth / 2 - CARD_WIDTH / 2 + (down ? xDelta : 0);
            const sc = down ? 1 - Math.abs(xDelta) / CARD_WIDTH / 5 : 1

            return { x, sc, display: 'block', immediate: false }
        });
    });

    // Use renderItem if provided, otherwise use default MealCard rendering
    const renderMealCard = (item, i) => {
        if (props.renderItem) {
            return props.renderItem(item, i);
        }

        // Default rendering with support for dynamic props
        return (
            <MealCard
                item={item}
                key={i}
                ref={el => item.element = el}
                items={props.items}
                currentIndex={i}
                thumped={props.thumped || false}
                activeFlipped={props.activeFlipped || false}
                activeDate={props.activeDate}
                activeMeal={props.activeMeal}
                activeItem={props.activeItem}
                currentMealType={props.currentMealType}
                onClickItemImage={props.onClickItemImage}
                scrollToNextCard={props.scrollToNextCard}
                toggleActiveFlipped={props.toggleActiveFlipped}
            />
        );
    };

    return (
        <div className="spring-feed" {...bind()}>
            {cards.map(({ x, display, sc }, i) => (
                <animated.div className="spring-feed-item" key={i} style={{ display, transform: x.interpolate(x => `translate3d(${x}px,0,0)`) }}>
                    <animated.div className="spring-feed-item-container" style={{ transform: sc.interpolate(s => `scale(${s})`)}}>
                        {renderMealCard(props.items[i], i)}
                    </animated.div>
                </animated.div>
            ))}
        </div>
    );
}
